import React, { useEffect, useRef, useState } from 'react';
import Icon from '../Icon';
import { useSiteContent } from '../SiteContent';
import { withEmphasis } from '../emphasis';

/**
 * Ein Text der Website — im Normalfall schlicht der Text, im Bearbeiten-Modus
 * eine bearbeitbare Stelle.
 *
 * Wichtig: Ist der Bearbeiten-Modus aus (also für jeden Besucher und für das
 * Prerendering), entsteht **kein zusätzliches Markup**. Die ausgelieferte Seite
 * sieht damit exakt so aus wie vor der Umstellung.
 *
 * Alles, was der Editor rendert, ist bewusst inline (span, button, textarea):
 * Die Textstellen stehen in Überschriften, Absätzen und Listeneinträgen, und ein
 * <div> in einem <p> zerreißt der Browser stillschweigend.
 */
const T: React.FC<{ k: string }> = ({ k }) => {
  const { t, editing } = useSiteContent();
  if (!editing) return <>{withEmphasis(t(k))}</>;
  return <EditableText textKey={k} />;
};

const EditableText: React.FC<{ textKey: string }> = ({ textKey }) => {
  const { t, raw, save, reset, isChanged, maxLength, labelOf } = useSiteContent();
  const [active, setActive] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const changed = isChanged(textKey);
  const max = maxLength(textKey);

  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);

  const open = (event: React.MouseEvent) => {
    // Die Textstellen sitzen in Links, Schaltflächen und <summary>: ohne das hier
    // wechselt der Klick auf den Stift die Seite oder klappt die Frage zu.
    event.preventDefault();
    event.stopPropagation();
    setValue(raw(textKey));
    setError(null);
    setActive(true);
  };

  const close = () => {
    setActive(false);
    setError(null);
  };

  const commit = async () => {
    setBusy(true);
    setError(null);
    try {
      await save(textKey, value);
      setActive(false);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    setError(null);
    try {
      await reset(textKey);
      setActive(false);
    } catch {
      setError('Zurücksetzen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  if (!active) {
    return (
      <span className={`site-edit${changed ? ' site-edit--changed' : ''}`}>
        {withEmphasis(t(textKey))}
        <button
          type="button"
          className="site-edit__pencil"
          onClick={open}
          title={`Text bearbeiten — ${labelOf(textKey)}`}
          aria-label={`Text bearbeiten: ${labelOf(textKey)}`}
        >
          <Icon name="pencil" size={12} />
        </button>
      </span>
    );
  }

  // Die Längenangabe ist eine Warnung, keine Sperre: Sie ist aus der Länge des
  // Originaltextes abgeleitet und kann im Einzelfall danebenliegen. Wer den Satz
  // trotzdem braucht, soll ihn schreiben dürfen — und sieht am roten Zähler, dass
  // er das Layout im Blick behalten muss. Die harte Grenze zieht der Server.
  const tooLong = value.length > max;
  const hasPlaceholder = /\{[a-z]+\}/.test(value);

  return (
    <span
      className="site-edit site-edit--active"
      // Gilt für alles im Editor: Ein Klick darin darf niemals die umgebende
      // Schaltfläche auslösen oder die aufgeklappte Frage wieder schließen.
      onClick={(event) => event.preventDefault()}
    >
      <span className="site-edit__key">{labelOf(textKey)}</span>
      <textarea
        ref={inputRef}
        className="site-edit__input"
        value={value}
        rows={Math.min(12, Math.max(2, Math.ceil(value.length / 60)))}
        disabled={busy}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') close();
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) commit();
        }}
      />
      <span className="site-edit__foot">
        <span
          className={`site-edit__count${tooLong ? ' site-edit__count--over' : ''}`}
          title={tooLong ? 'Länger als vorgesehen — das Layout kann darunter leiden.' : undefined}
        >
          {value.length}/{max} Zeichen
        </span>
        <button type="button" className="site-edit__btn site-edit__btn--primary" onClick={commit} disabled={busy}>
          Speichern
        </button>
        <button type="button" className="site-edit__btn" onClick={close} disabled={busy}>
          Abbrechen
        </button>
        {changed && (
          <button type="button" className="site-edit__btn" onClick={restore} disabled={busy}>
            <Icon name="rotate-ccw" size={13} />
            Original
          </button>
        )}
      </span>
      <span className="site-edit__hint">
        Sternchen wie **fett** zeichnen Text fett aus. Eine Leerzeile trennt Absätze,
        Enter bricht die Zeile um. Esc bricht ab, Strg+Enter speichert.
        {hasPlaceholder && ' Der Ausdruck in geschweiften Klammern ist ein Platzhalter — er wird auf der Seite automatisch gefüllt.'}
      </span>
      {error && <span className="site-edit__error">{error}</span>}
    </span>
  );
};

export default T;
